(function () {
  'use strict';

  const DATE_ATTRIBUTE_KEYS = [
    '配送希望日',
    'open_logi_date',
    'delivery_date'
  ];
  const TIME_ATTRIBUTE_KEYS = [
    '配送希望時間',
    'open_logi_time',
    'delivery_time'
  ];
  const WEEKDAYS_JA = ['日', '月', '火', '水', '木', '金', '土'];
  const TIME_LABELS = {
    '': '',
    '0812': '午前中',
    '1416': '14時〜16時',
    '1618': '16時〜18時',
    '1820': '18時〜20時',
    '1921': '19時〜21時'
  };
  const OPEN_LOGI_TIMES = {
    '': '',
    '0812': '8',
    '1416': '14',
    '1618': '16',
    '1820': '18',
    '1921': '19'
  };
  const DELIVERY_TIMES = {
    '': '',
    '0812': '08時-12時',
    '1416': '14時-16時',
    '1618': '16時-18時',
    '1820': '18時-20時',
    '1921': '19時-21時'
  };

  function parseIsoDate(value) {
    const match = String(value || '').match(/(\d{4})[/-](\d{2})[/-](\d{2})/);
    if (!match) return '';
    const iso = `${match[1]}-${match[2]}-${match[3]}`;
    const date = new Date(`${iso}T00:00:00Z`);
    return Number.isNaN(date.getTime()) ? '' : iso;
  }

  function tokyoTodayIso(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  }

  function addDays(iso, days) {
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  }

  function displayDate(iso) {
    if (!iso) return '';
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}(${WEEKDAYS_JA[date.getUTCDay()]})`;
  }

  function resolveTimeCode(state) {
    const rawValues = [state.timeDisplay, state.deliveryTime, state.openLogiTime].map((value) => String(value || ''));
    for (const [code, label] of Object.entries(TIME_LABELS)) {
      if (code && rawValues.some((value) => value === code || value === label || value.replace(/〜/g, '-') === label.replace(/〜/g, '-'))) return code;
    }
    for (const [code, label] of Object.entries(DELIVERY_TIMES)) {
      if (code && rawValues.includes(label)) return code;
    }
    for (const [code, label] of Object.entries(OPEN_LOGI_TIMES)) {
      if (code && rawValues.includes(label)) return code;
    }
    return '';
  }

  function dateAttributes(iso) {
    if (!iso) {
      return {
        '配送希望日': '',
        open_logi_date: '',
        delivery_date: ''
      };
    }
    return {
      '配送希望日': displayDate(iso),
      open_logi_date: iso,
      delivery_date: iso.replace(/-/g, '/')
    };
  }

  function timeAttributes(code) {
    const safeCode = Object.prototype.hasOwnProperty.call(TIME_LABELS, code) ? code : '';
    return {
      '配送希望時間': TIME_LABELS[safeCode],
      open_logi_time: OPEN_LOGI_TIMES[safeCode],
      delivery_time: DELIVERY_TIMES[safeCode]
    };
  }

  function blankDeliveryAttributes() {
    return Object.fromEntries([...DATE_ATTRIBUTE_KEYS, ...TIME_ATTRIBUTE_KEYS].map((key) => [key, '']));
  }

  function cartUpdateUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.endsWith('/') ? root : `${root}/`}cart/update.js`;
  }

  class OKWDeliveryDateTime extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.blocked = this.dataset.blocked === 'true';
      this.minDays = Number.parseInt(this.dataset.minDays || '3', 10);
      this.maxDays = Number.parseInt(this.dataset.maxDays || '30', 10);
      this.status = this.querySelector('[data-okw-delivery-status]');
      this.dateField = this.querySelector('[data-okw-delivery-date]');
      this.timeField = this.querySelector('[data-okw-delivery-time]');
      this.noteField = this.querySelector('[data-okw-delivery-note]');
      this.pendingUpdate = Promise.resolve();
      this.noteTimer = null;
      this.state = this.readState();

      if (this.noteField) this.noteField.value = String(this.state.note || '');

      if (this.blocked) {
        if (this.dataset.hasStaleDeliveryAttributes === 'true') {
          this.updateAttributes(blankDeliveryAttributes())
            .then(() => this.setStatus('選択済みのお届け日時を解除しました。'))
            .catch(() => this.setStatus('お届け日時の解除に失敗しました。再読み込みしてお試しください。', true));
        }
      } else {
        this.prepareAllowedFields();
      }

      this.prepareNoteField();
    }

    readState() {
      try {
        return JSON.parse(this.querySelector('[data-okw-delivery-state]')?.textContent || '{}');
      } catch (_error) {
        return {};
      }
    }

    prepareAllowedFields() {
      if (!this.dateField || !this.timeField) return;

      const today = tokyoTodayIso();
      const min = addDays(today, this.minDays);
      const max = addDays(today, this.maxDays);
      this.dateField.min = min;
      this.dateField.max = max;

      const existingDate = parseIsoDate(this.state.openLogiDate || this.state.deliveryDate || this.state.dateDisplay);
      if (existingDate && existingDate >= min && existingDate <= max) {
        this.dateField.value = existingDate;
      } else if (existingDate) {
        this.dateField.value = '';
        this.updateAttributes(dateAttributes(''))
          .then(() => this.setStatus('選択期限を過ぎたお届け希望日を解除しました。'))
          .catch(() => this.setStatus('お届け希望日の解除に失敗しました。再読み込みしてお試しください。', true));
      }

      this.timeField.value = resolveTimeCode(this.state);
      this.dateField.addEventListener('change', () => this.saveDate());
      this.timeField.addEventListener('change', () => this.saveTime());
    }

    prepareNoteField() {
      if (!this.noteField) return;
      this.noteField.addEventListener('input', () => {
        window.clearTimeout(this.noteTimer);
        this.noteTimer = window.setTimeout(() => {
          this.updateAttributes({ biko: this.noteField.value }, this.noteField.value)
            .then(() => this.setStatus('備考を保存しました。'))
            .catch(() => this.setStatus('備考の保存に失敗しました。', true));
        }, 400);
      });
    }

    saveDate() {
      const iso = parseIsoDate(this.dateField?.value || '');
      if (iso && (iso < this.dateField.min || iso > this.dateField.max)) {
        this.dateField.value = '';
        this.setStatus('選択可能な期間内の日付を選んでください。', true);
        return this.updateAttributes(dateAttributes(''));
      }
      return this.updateAttributes(dateAttributes(iso))
        .then(() => this.setStatus(iso ? 'お届け希望日を保存しました。' : 'お届け希望日を解除しました。'))
        .catch(() => this.setStatus('お届け希望日の保存に失敗しました。', true));
    }

    saveTime() {
      const code = this.timeField?.value || '';
      return this.updateAttributes(timeAttributes(code))
        .then(() => this.setStatus(code ? 'お届け希望時間を保存しました。' : 'お届け希望時間を解除しました。'))
        .catch(() => this.setStatus('お届け希望時間の保存に失敗しました。', true));
    }

    flush() {
      window.clearTimeout(this.noteTimer);
      const attributes = this.blocked
        ? blankDeliveryAttributes()
        : {
            ...dateAttributes(parseIsoDate(this.dateField?.value || '')),
            ...timeAttributes(this.timeField?.value || '')
          };
      const note = this.noteField ? this.noteField.value : undefined;
      if (this.noteField) attributes.biko = note;
      this.setStatus('ご注文内容を確認しています。');
      return this.updateAttributes(attributes, note);
    }

    updateAttributes(attributes, note) {
      const task = async () => {
        const payload = { attributes };
        if (typeof note === 'string') payload.note = note;
        const response = await fetch(cartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error(`cart_update_failed:${response.status}`);
        return response.json();
      };
      this.pendingUpdate = this.pendingUpdate.catch(() => undefined).then(task);
      return this.pendingUpdate;
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', isError);
    }
  }

  async function handleCheckout(event, button) {
    if (button.dataset.okwDeliveryCheckoutPending === 'true') return;
    const scope = button.closest('#AjaxCartSubtotal') || document;
    const component = scope.querySelector('okw-delivery-datetime');
    if (!component || typeof component.flush !== 'function') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    button.dataset.okwDeliveryCheckoutPending = 'true';
    button.setAttribute('aria-busy', 'true');

    try {
      await component.flush();
      const root = window.Shopify?.routes?.root || '/';
      window.location.assign(`${root.endsWith('/') ? root : `${root}/`}checkout`);
    } catch (_error) {
      delete button.dataset.okwDeliveryCheckoutPending;
      button.removeAttribute('aria-busy');
      component.setStatus('お届け日時を保存できなかったため、購入手続きへ進めませんでした。もう一度お試しください。', true);
    }
  }

  if (!customElements.get('okw-delivery-datetime')) {
    customElements.define('okw-delivery-datetime', OKWDeliveryDateTime);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (button) void handleCheckout(event, button);
  }, true);

  window.OKWDeliveryDateTime = {
    addDays,
    blankDeliveryAttributes,
    dateAttributes,
    displayDate,
    parseIsoDate,
    resolveTimeCode,
    timeAttributes,
    tokyoTodayIso
  };
}());
